import { useRef } from 'react';
import type React from 'react';
import { RentReportingAuthBoundary } from '../contexts/RentReportingAuthProvider';
import { EnrollmentRedirectHandler } from './EnrollmentRedirectHandler';
import { zillowRedirectLogin } from '@zg-rentals/login-zillow/client';
import { emitAuthStartEvent } from '../analytics/events';
import type { ClickstreamAuthTriggerSource } from '../analytics/events';
import { isNativeWebView, launchNativeLogin } from '../utils/nativeBridge';
interface RentReportingBoundaryProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  authTriggerSource?: ClickstreamAuthTriggerSource;
}

export const RentReportingBoundary: React.FC<RentReportingBoundaryProps> = ({
  children,
  requireAuth = false,
  authTriggerSource = undefined,
}) => {
  const hasEmittedAuthStart = useRef(false);

  if (!requireAuth) {
    return <EnrollmentRedirectHandler>{children}</EnrollmentRedirectHandler>;
  }

  return (
    <RentReportingAuthBoundary
      onUnauthorized={() => {
        const doLogin = async () => {
          // Only emit auth start event once
          if (authTriggerSource && !hasEmittedAuthStart.current) {
            emitAuthStartEvent(authTriggerSource);
            hasEmittedAuthStart.current = true;
          }

          const currentUrl = new URL(window.location.href);
          if (authTriggerSource) {
            currentUrl.searchParams.set('auth_trigger', authTriggerSource);
          }

          if (isNativeWebView()) {
            window.history.replaceState({}, '', currentUrl.toString());
            launchNativeLogin();
            return;
          }

          const pathWithQuery = currentUrl.pathname + currentUrl.search;

          zillowRedirectLogin({
            entryPoint: 'rent-reporting-web',
            baseURL: process.env.NEXT_PUBLIC_AUTH_BASE_URL,
            forwardPrefix: process.env.NEXT_PUBLIC_AUTH_FORWARD_PREFIX,
            redirectPath: pathWithQuery,
          });
        };
        doLogin();

        return null;
      }}
    >
      <EnrollmentRedirectHandler>{children}</EnrollmentRedirectHandler>
    </RentReportingAuthBoundary>
  );
};
